// @ts-check
// Barras do editor de bots (contrato de bots, seção 22.2), como as dos fluxos. Barra 1: voltar para os bots, o nome
// com o lápis, a situação, "Estatísticas" e "Sair do editor". Barra 2: as abas Editor e Configurações (ponto âmbar na
// aba com pendência), "Testar bot", o botão das pendências com a lista agrupada, "Alterações não salvas" e salvar e
// ativar (ou "Salvar alterações" e o menu no bot ativo). No celular, uma barra só com o menu.
import { html } from 'htm/preact';
import { useRef } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { Dropdown } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';
import { groupBotProblems } from '../../lib/bot-validation.js';
import { FlowMenuButton } from '../flows/flow-menu.js';
import { BotStatusChip, botProblemText } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/bot-validation.js').BotEditorProblem} BotEditorProblem
 * @typedef {import('../../data/bots.js').BotDetail} BotDetail
 * @typedef {import('../flows/flow-menu.js').FlowMenuItem} FlowMenuItem
 * @typedef {'editor' | 'settings'} BotEditorTab
 */

export const BOT_EDITOR_TABS = /** @type {ReadonlyArray<BotEditorTab>} */ (Object.freeze(['editor', 'settings']));

/** Aba da URL (`?tab=`), editor por padrão. @param {URLSearchParams} parameters @returns {BotEditorTab} */
export const readBotEditorTab = (parameters) => (parameters.get('tab') === 'settings' ? 'settings' : 'editor');

const GROUP_KEYS = /** @type {const} */ (['TRIGGERS', 'NODE', 'SETTINGS', 'BOT']);

/** Lugar e texto de uma pendência (o que aparece uma vez só na lista). @param {BotEditorProblem} problem */
const problemPlaceKey = (problem) => [problem.area, problem.nodeId ?? '', problem.triggerId ?? '', botProblemText(problem)].join('|');

/**
 * Botão "2 pendências" e a lista agrupada; um item leva ao lugar dele.
 * @param {{ problems: BotEditorProblem[], isOpen: boolean, onOpenChange: (isOpen: boolean) => void, onGo: (problem: BotEditorProblem) => void,
 *   nodeTitle: (nodeId: string) => string }} props
 */
export function BotProblemsButton({ problems: allProblems, isOpen, onOpenChange, onGo, nodeTitle }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  /** @type {Map<string, BotEditorProblem>} */
  const byPlace = new Map();
  for (const problem of allProblems) {
    const key = problemPlaceKey(problem);
    const current = byPlace.get(key);
    if (!current || (current.severity !== 'ERROR' && problem.severity === 'ERROR')) byPlace.set(key, problem);
  }
  const problems = [...byPlace.values()];
  if (problems.length === 0) return null;
  const groups = groupBotProblems(problems);
  const hasError = problems.some((problem) => problem.severity === 'ERROR');
  return html`<button type="button" ref=${anchorRef} class="flow-problems-button" data-severity=${hasError ? 'error' : 'warning'} aria-haspopup="dialog"
      aria-expanded=${isOpen ? 'true' : 'false'} onClick=${() => onOpenChange(!isOpen)} data-testid="bot-problems-button">
      <${Icon} name=${hasError ? 'IconAlertCircle' : 'IconAlertTriangle'} size=${16} />
      <span>${t('bots.editor.problems', { count: problems.length })}</span>
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => onOpenChange(false)} placement="bottom-end" width=${360} role="dialog">
      <div class="flow-problems-list" aria-label=${t('bots.editor.problemsTitle')}>
        ${GROUP_KEYS.map((key) => (groups[key].length === 0 ? null : html`<section key=${key} class="flow-problems-group">
          <h3 class="flow-problems-group-title">${t(`bots.editor.problemGroups.${key}`)}</h3>
          <ul>
            ${groups[key].map((problem) => html`<li key=${problemPlaceKey(problem)}>
              <button type="button" class="flow-problems-item" data-severity=${problem.severity === 'ERROR' ? 'error' : 'warning'}
                onClick=${() => {
                  onOpenChange(false);
                  onGo(problem);
                }}>
                <${Icon} name=${problem.severity === 'ERROR' ? 'IconAlertCircle' : 'IconAlertTriangle'} size=${14} />
                <span class="flow-problems-item-texts">
                  ${problem.nodeId ? html`<span class="flow-problems-item-step">${nodeTitle(problem.nodeId)}</span>` : null}
                  <span>${botProblemText(problem)}</span>
                </span>
              </button>
            </li>`)}
          </ul>
        </section>`))}
      </div>
    <//>`;
}

/**
 * @param {{ bot: BotDetail, tab: BotEditorTab, problems: BotEditorProblem[], isDirty: boolean, isSaving: boolean, isMobile: boolean, problemsOpen: boolean,
 *   onProblemsOpenChange: (isOpen: boolean) => void, onGoToProblem: (problem: BotEditorProblem) => void, nodeTitle: (nodeId: string) => string,
 *   onTab: (tab: BotEditorTab) => void, onBack: () => void, onExit: () => void, onStats: (() => void) | null, onRename: () => void, onTest: () => void,
 *   isTesting: boolean, onSave: () => void, onSaveAndActivate: () => void, menuItems: FlowMenuItem[] }} props
 */
export function BotEditorBars({
  bot, tab, problems, isDirty, isSaving, isMobile, problemsOpen, onProblemsOpenChange, onGoToProblem, nodeTitle, onTab, onBack, onExit, onStats, onRename, onTest,
  isTesting, onSave, onSaveAndActivate, menuItems,
}) {
  const isActive = bot.status === 'ACTIVE';
  const settingsHasProblem = problems.some((problem) => problem.area === 'SETTINGS');
  const editorHasProblem = problems.some((problem) => problem.area !== 'SETTINGS');
  if (isMobile) {
    return html`<header class="flow-editor-bar flow-editor-bar-mobile">
      <button type="button" class="flow-icon-button" aria-label=${t('bots.editor.back')} onClick=${onBack}><${Icon} name="IconChevronLeft" size=${20} /></button>
      <button type="button" class="flow-editor-name" onClick=${onRename} title=${bot.name}><span class="flow-editor-name-text">${bot.name}</span></button>
      <${BotStatusChip} status=${bot.status} inactiveReason=${bot.inactiveReason} />
      ${problems.length > 0
        ? html`<${BotProblemsButton} problems=${problems} isOpen=${problemsOpen} onOpenChange=${onProblemsOpenChange} onGo=${onGoToProblem} nodeTitle=${nodeTitle} />`
        : null}
      <${FlowMenuButton} ariaLabel=${t('bots.editor.moreActions')} icon="IconDotsVertical" items=${[
        { key: 'test', text: t('bots.editor.test'), icon: 'IconMessageCircle', onSelect: onTest },
        { key: 'settings', text: t('bots.editor.tabs.settings'), icon: 'IconSettings', onSelect: () => onTab('settings') },
        ...(isActive ? [] : [{ key: 'activate', text: t('bots.editor.saveAndActivate'), icon: 'IconPlayerPlay', onSelect: onSaveAndActivate }]),
        ...menuItems,
        { key: 'leave', text: t('bots.editor.exit'), icon: 'IconLogout', separatorBefore: true, onSelect: onExit },
      ]} />
    </header>`;
  }
  return html`<header class="flow-editor-bar flow-editor-bar-top">
      <div class="flow-editor-bar-left">
        <button type="button" class="flow-back-link" onClick=${onBack}><${Icon} name="IconChevronLeft" size=${16} /><span>${t('bots.list.title')}</span></button>
        <span class="flow-editor-divider" aria-hidden="true"></span>
        <button type="button" class="flow-editor-name" onClick=${onRename} title=${t('bots.editor.rename')} data-testid="bot-editor-name">
          <span class="flow-editor-name-text">${bot.name}</span><${Icon} name="IconPencil" size=${14} />
        </button>
        <${BotStatusChip} status=${bot.status} inactiveReason=${bot.inactiveReason} />
      </div>
      <div class="flow-editor-bar-right">
        ${onStats ? html`<button type="button" class="flow-link-button" onClick=${onStats}><${Icon} name="IconChartBar" size=${16} />${t('bots.editor.stats')}</button>` : null}
        ${onStats ? html`<span class="flow-editor-divider" aria-hidden="true"></span>` : null}
        <button type="button" class="flow-editor-exit" onClick=${onExit}>${t('bots.editor.exit')}</button>
      </div>
    </header>
    <div class="flow-editor-bar flow-editor-bar-tabs">
      <div class="flow-editor-tabs" role="tablist" aria-label=${t('bots.editor.tabsLabel')}>
        ${BOT_EDITOR_TABS.map((key) => html`<button type="button" key=${key} role="tab" class="flow-editor-tab" aria-selected=${tab === key ? 'true' : 'false'}
          data-active=${tab === key ? 'true' : undefined} onClick=${() => onTab(key)} data-testid=${`bot-tab-${key}`}>
          <span>${t(`bots.editor.tabs.${key}`)}</span>
          ${(key === 'settings' ? settingsHasProblem : editorHasProblem) ? html`<span class="flow-editor-tab-dot" aria-label=${t('bots.editor.tabHasProblems')}></span>` : null}
        </button>`)}
      </div>
      <div class="flow-editor-bar-right">
        <${Button} title=${t('bots.editor.test')} icon="IconMessageCircle" variant=${isTesting ? 'secondary' : 'tertiary'} size="small" onClick=${onTest} data-testid="bot-test-button" />
        <${BotProblemsButton} problems=${problems} isOpen=${problemsOpen} onOpenChange=${onProblemsOpenChange} onGo=${onGoToProblem} nodeTitle=${nodeTitle} />
        ${isDirty ? html`<span class="flow-editor-unsaved" role="status"><span class="flow-editor-unsaved-dot" aria-hidden="true"></span>${t('bots.editor.unsaved')}</span>` : null}
        ${isActive
          ? html`<${Button} title=${t('bots.editor.saveChanges')} accent="blue" size="small" disabled=${!isDirty} isLoading=${isSaving} onClick=${onSave} hotkeyLabel="Ctrl S"
              data-testid="bot-save-button" />`
          : html`<${Button} title=${t('bots.editor.save')} variant="tertiary" size="small" isLoading=${isSaving} onClick=${onSave} data-testid="bot-save-button" />
            <${Button} title=${t('bots.editor.saveAndActivate')} accent="blue" size="small" disabled=${isSaving} onClick=${onSaveAndActivate} data-testid="bot-activate-button" />`}
        ${menuItems.length > 0 ? html`<${FlowMenuButton} ariaLabel=${t('bots.editor.moreActions')} icon="IconDots" items=${menuItems} className="flow-menu-trigger flow-editor-menu" />` : null}
      </div>
    </div>`;
}

/** Faixa "Há alterações não salvas deste bot neste navegador." @param {{ onRestore: () => void, onDiscard: () => void }} props */
export function BotDraftBanner({ onRestore, onDiscard }) {
  return html`<div class="flow-draft-banner" role="status">
    <${Icon} name="IconDeviceFloppy" size=${16} />
    <span>${t('bots.editor.localDraft')}</span>
    <${Button} title=${t('bots.editor.restore')} variant="secondary" size="small" onClick=${onRestore} />
    <${Button} title=${t('bots.editor.discard')} variant="tertiary" size="small" onClick=${onDiscard} />
  </div>`;
}
